/**
 * ============================================================================
 * FIRE SAFETY SCANNER - API SERVICE
 * ============================================================================
 * Centralized API service for all backend communication
 * Version: v3.10.0-CLEAR-MAP
 * 
 * All fetch calls extracted from App.js
 * Makes it easy to:
 * - Update API endpoints
 * - Add error handling
 * - Add request/response logging
 * - Mock API calls for testing
 */

import { BACKEND_URL } from '../utils/constants';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get session token from localStorage
 */
const getSessionToken = () => {
  return localStorage.getItem('session_token');
};

/**
 * Detect if running on localhost for local-dev bypass headers.
 */
const isLocalHost = () => {
  try {
    if (window.Capacitor?.isNativePlatform?.()) return false;
    if (typeof window === 'undefined' || !window.location) return false;
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
  } catch {
    return false;
  }
};

/**
 * Build headers with session token and Authorization Bearer if available
 */
export const buildHeaders = (additionalHeaders = {}) => {
  const sessionToken = getSessionToken();
  const token = localStorage.getItem('token');
  const headers = {
    ...additionalHeaders
  };

  if (isLocalHost()) {
    headers['X-Local-Dev'] = 'true';
  }
  
  if (sessionToken) {
    headers['session-token'] = sessionToken;
  }
  
  // Add Bearer token for admin AND protected user endpoints
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKey) {
    headers['X-API-Key'] = apiKey;
  }
  
  return headers;
};

const base64ToBlob = (base64Data, mimeType = 'image/jpeg') => {
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
};

/**
 * Call a protected user endpoint (requires MSAL token)
 * Automatically handles token refresh if expired (401 response)
 * 
 * @param {string} endpoint - API endpoint (relative or absolute)
 * @param {object} options - Fetch options (method, body, etc.)
 * @param {function} getTokenFn - Optional function to get fresh token (from useProtectedFeature)
 * @returns {Promise<Response>} - Fetch response
 */
export const callProtectedEndpoint = async (endpoint, options = {}, getTokenFn = null) => {
  const url = endpoint.startsWith('http') ? endpoint : `${BACKEND_URL}${endpoint}`;
  
  // Build headers with current token
  const headers = buildHeaders(options.headers || {});
  
  const response = await fetch(url, {
    ...options,
    headers
  });
  
  // If 401 (token expired) and we have a token refresh function, retry once
  if (response.status === 401 && getTokenFn) {
    try {
      const newToken = await getTokenFn();
      if (newToken) {
        localStorage.setItem('token', newToken);
        
        // Retry with new token
        const retryHeaders = buildHeaders(options.headers || {});
        return await fetch(url, {
          ...options,
          headers: retryHeaders
        });
      }
    } catch {
      // Token refresh failed
    }
  }
  
  return response;
};

// ============================================================================
// MICRO-CACHE for Admin API Calls (60-second TTL)
// ============================================================================

/**
 * Simple in-memory cache to prevent redundant admin API calls during tab switching.
 * Only caches GET requests to /api/admin/* endpoints.
 * TTL: 60 seconds
 * Max entries: 50
 */
const API_CACHE = new Map();
const CACHE_TTL = 60 * 1000; // 60 seconds

function getCacheKey(url, options) {
  return `${url}|${JSON.stringify(options || {})}`;
}

function getCached(key) {
  const cached = API_CACHE.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  if (cached) {
    API_CACHE.delete(key); // Expired
  }
  return null;
}

function setCache(key, data) {
  API_CACHE.set(key, { data, timestamp: Date.now() });
  // Cleanup: max 50 entries (FIFO)
  if (API_CACHE.size > 50) {
    const first = API_CACHE.keys().next().value;
    API_CACHE.delete(first);
  }
}

/**
 * Clear cache manually (for forced refresh)
 */
export function clearCache() {
  API_CACHE.clear();
}

/**
 * Strip X-Local-Dev header outside of localhost to keep WAF-safe requests.
 */
const stripLocalDevHeader = (options = {}) => {
  if (isLocalHost() || !options || !options.headers) {
    return options;
  }

  const headers = options.headers;

  if (headers instanceof Headers) {
    headers.delete('X-Local-Dev');
    return { ...options, headers };
  }

  if (Array.isArray(headers)) {
    const filtered = headers.filter(([key]) => String(key).toLowerCase() !== 'x-local-dev');
    return { ...options, headers: filtered };
  }

  if (typeof headers === 'object') {
    const { ['X-Local-Dev']: _ignored, ['x-local-dev']: _ignoredLower, ...rest } = headers;
    return { ...options, headers: rest };
  }

  return options;
};

/**
 * Cached fetch wrapper - Only for GET requests to /api/admin/*
 * Reduces redundant Cosmos DB queries when switching admin tabs.
 * 
 * Usage: Replace `fetch(url)` with `cachedFetch(url)` in admin pages
 * Or: Globally intercept admin GETs (transparent caching)
 */
const cachedFetch = async (url, options = {}) => {
  const isGet = !options.method || options.method === 'GET';
  const isAdmin = url.includes('/api/admin/');
  const forceRefresh = url.includes('fresh=1') || url.includes('force=1');
  
  // Only cache GET requests to admin endpoints
  if (isGet && isAdmin && !forceRefresh) {
    const key = getCacheKey(url, options);
    const cached = getCached(key);
    
    if (cached) {
      // Return cached response (clone to simulate fetch API)
      const jsonStr = JSON.stringify(cached);
      return {
        ok: true,
        status: 200,
        json: async () => cached,
        text: async () => jsonStr,
        clone: function() { return this; }
      };
    }
    
    // Cache miss - fetch from server
    const response = await originalFetch(url, options);
    
    if (response.ok) {
      const data = await response.json();
      setCache(key, data);
      const jsonStr = JSON.stringify(data);
      // Return response-like object
      return {
        ok: true,
        status: response.status,
        json: async () => data,
        text: async () => jsonStr,
        clone: function() { return this; }
      };
    }
    
    return response; // Return error responses as-is
  }
  
  // Non-cacheable request (POST, PUT, DELETE, or non-admin)
  return originalFetch(url, options);  // ✅ Use originalFetch to avoid recursion
};

// 🌐 Global fetch wrapper - Transparently cache admin GET requests
const originalFetch = window.fetch;
window.fetch = function(url, options) {
  const sanitizedOptions = stripLocalDevHeader(options);
  // Only intercept if URL is a string and contains admin endpoint
  if (typeof url === 'string' && url.includes('/api/admin/')) {
    return cachedFetch(url, sanitizedOptions);
  }
  return originalFetch.call(this, url, sanitizedOptions);
};

// ============================================================================
// AUTHENTICATION API
// ============================================================================

/**
 * SECURITY: Demo login removed - this function is disabled
 * Authentication must go through Microsoft Entra ID (MSAL)
 * Kept for API compatibility but always fails
 */
export const handleLogin = async (/* username, password */) => {
  console.error('❌ SECURITY: Demo login is disabled. Use Microsoft Entra ID authentication.');
  throw new Error('Demo login is disabled. Please use Microsoft authentication.');
};

/**
 * Check/validate session
 */
export const checkSession = async (sessionId) => {
  const formData = new FormData();
  formData.append('session_id', sessionId);
  
  const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
    method: 'POST',
    body: formData
  });
  
  if (!response.ok) {
    throw new Error('Session validation failed');
  }
  
  return await response.json();
};

/**
 * Microsoft OAuth authentication
 */
export const authenticateWithMicrosoft = async (idToken) => {
  const response = await fetch(`${BACKEND_URL}/api/auth/microsoft`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ id_token: idToken })
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || 'Backend authentication failed');
  }
  
  return await response.json();
};

// ============================================================================
// INSPECTIONS API
// ============================================================================

/**
 * Load inspections with pagination (optimized for speed)
 */
export const loadInspections = async (limit = 20, skip = 0) => {
  const response = await fetch(`${BACKEND_URL}/api/inspections?limit=${limit}&skip=${skip}`, {
    headers: buildHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to load inspections');
  }
  
  return await response.json();
};

/**
 * Load due inspections
 */
export const loadDueInspections = async () => {
  const response = await fetch(`${BACKEND_URL}/api/inspections/due`, {
    headers: buildHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to load due inspections');
  }
  
  return await response.json();
};

/**
 * Create new inspection (analyze image)
 * This is used for both regular and quick shot submissions
 */
export const createInspection = async (inspectionData) => {
  if (inspectionData?.image_base64) {
    const formData = new FormData();
    const imageBlob = base64ToBlob(inspectionData.image_base64);
    formData.append('image', imageBlob, 'inspection.jpg');

    if (inspectionData?.thumbnail_base64) {
      const thumbnailBlob = base64ToBlob(inspectionData.thumbnail_base64);
      formData.append('thumbnail', thumbnailBlob, 'inspection-thumbnail.jpg');
    }

    // 🔒 WAF-SAFE: Base64 encode GPS and address (per David's recommendation)
    const sensitiveFields = ['gps_data', 'business_address'];
    
    Object.entries(inspectionData || {}).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (key === 'image_base64' || key === 'thumbnail_base64') return;
      
      // Encode sensitive fields - send ONLY encoded version to avoid WAF blocking
      if (sensitiveFields.includes(key)) {
        const jsonString = typeof value === 'object' ? JSON.stringify(value) : String(value);
        const encoded = btoa(jsonString); // Base64 encode
        formData.append(`${key}_encoded`, encoded);
        formData.append(`${key}_encoding`, 'base64');
        // Do NOT send original - WAF will block it
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, String(value));
      }
    });

    const response = await fetch(`${BACKEND_URL}/api/inspections/upload`, {
      method: 'POST',
      headers: buildHeaders(),
      body: formData
    });
    return response;
  }

  const response = await fetch(`${BACKEND_URL}/api/inspections`, {
    method: 'POST',
    headers: buildHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify(inspectionData)
  });
  
  // Don't throw on error - let caller handle it
  return response;
};

/**
 * Update/save inspection changes
 */
export const saveInspectionChanges = async (inspectionId, updates) => {
  const response = await fetch(`${BACKEND_URL}/api/inspections/${inspectionId}`, {
    method: 'PUT',
    headers: buildHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    throw new Error('Failed to save inspection changes');
  }
  
  return await response.json();
};

/**
 * Delete inspection
 */
export const deleteInspection = async (inspectionId) => {
  const response = await fetch(`${BACKEND_URL}/api/inspections/${inspectionId}`, {
    method: 'DELETE',
    headers: buildHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to delete inspection');
  }
  
  return await response.json();
};

/**
 * Update inspection (for editing from frontend)
 */
export const updateInspection = async (inspectionId, updateData) => {
  // User-facing edit flow: use UUID-based PATCH endpoint (no-auth)
  const url = `${BACKEND_URL}/api/inspections/${inspectionId}/edit`;

  // Retry on Cosmos throttling (429/16500) — keeps UX smooth during RU spikes.
  for (let attempt = 0; attempt < 3; attempt++) {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: buildHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(updateData)
    });

    if (response.ok) {
      return await response.json();
    }

    const rawText = await response.text().catch(() => '');
    const lower = rawText.toLowerCase();
    const isRateLimited = response.status === 429 || lower.includes('requestratetoolarge') || lower.includes('code\":16500') || lower.includes('code: 16500');

    if (isRateLimited && attempt < 2) {
      const delayMs = 300 * (2 ** attempt);
      await new Promise((r) => setTimeout(r, delayMs));
      continue;
    }

    // Try to parse FastAPI JSON error body; fallback to raw text
    try {
      const err = rawText ? JSON.parse(rawText) : {};
      throw new Error(err.detail || err.message || rawText || `Failed to update inspection (${response.status})`);
    } catch (_) {
      throw new Error(rawText || `Failed to update inspection (${response.status})`);
    }
  }
};

/**
 * User approves their own submission (UUID-based)
 */
export const userApproveInspection = async (inspectionId, approvalData = {}) => {
  const url = `${BACKEND_URL}/api/inspections/${inspectionId}/user-approve`;

  for (let attempt = 0; attempt < 3; attempt++) {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(approvalData)
    });

    if (response.ok) {
      return await response.json();
    }

    const errText = await response.text().catch(() => '');
    const lower = errText.toLowerCase();
    const isRateLimited = response.status === 429 || lower.includes('requestratetoolarge') || lower.includes('code\":16500') || lower.includes('code: 16500');

    if (isRateLimited && attempt < 2) {
      const delayMs = 400 * (2 ** attempt);
      await new Promise((r) => setTimeout(r, delayMs));
      continue;
    }

    throw new Error(errText || `Failed to approve inspection (${response.status})`);
  }
};

/**
 * User reject inspection (self-service)
 */
export const userRejectInspection = async (inspectionId, rejectionData = {}) => {
  const url = `${BACKEND_URL}/api/inspections/${inspectionId}/user-reject`;

  for (let attempt = 0; attempt < 3; attempt++) {
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders({
        'Content-Type': 'application/json'
      }),
      body: JSON.stringify(rejectionData)
    });

    if (response.ok) {
      return await response.json();
    }

    const errText = await response.text().catch(() => '');
    const lower = errText.toLowerCase();
    const isRateLimited = response.status === 429 || lower.includes('requestratetoolarge') || lower.includes('code\":16500') || lower.includes('code: 16500');

    if (isRateLimited && attempt < 2) {
      const delayMs = 400 * (2 ** attempt);
      await new Promise((r) => setTimeout(r, delayMs));
      continue;
    }

    throw new Error(errText || `Failed to reject inspection (${response.status})`);
  }
};

/**
 * Approve inspection
 */
export const approveInspection = async (inspectionId, approvalData) => {
  const response = await fetch(`${BACKEND_URL}/api/inspections/${inspectionId}/approve`, {
    method: 'POST',
    headers: buildHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify(approvalData)
  });
  
  if (!response.ok) {
    throw new Error('Failed to approve inspection');
  }
  
  return await response.json();
};

/**
 * Toggle human review flag
 */
export const toggleHumanReview = async (inspectionId, reviewStatus, reviewerName = 'Admin User') => {
  const response = await fetch(`${BACKEND_URL}/api/inspections/${inspectionId}/review`, {
    method: 'POST',
    headers: buildHeaders({
      'Content-Type': 'application/json'
    }),
    body: JSON.stringify({
      review_status: reviewStatus,
      reviewer_name: reviewerName
    })
  });
  
  if (!response.ok) {
    throw new Error('Failed to toggle review status');
  }
  
  return await response.json();
};

// ============================================================================
// GAMIFICATION API
// ============================================================================

/**
 * Fetch user gamification stats
 * Uses Base64 encoding to prevent WAF blocking on email patterns
 */
export const fetchUserGamificationStats = async (userEmail) => {
  // Base64 encode the email to bypass WAF
  const encodedEmail = btoa(userEmail);
  
  const response = await callProtectedEndpoint(
    `/api/gamification/user-stats/${encodedEmail}`,
    { method: 'GET' }
  );
  
  if (!response.ok) {
    throw new Error('Failed to fetch gamification stats');
  }
  
  return await response.json();
};

/**
 * Award gamification points
 */
export const awardGamificationPoints = async (userEmail, action = 'scan') => {
  const response = await callProtectedEndpoint(
    `/api/gamification/award-points`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        user_email: userEmail,
        action: action
      })
    }
  );
  
  if (!response.ok) {
    throw new Error('Failed to award points');
  }
  
  return await response.json();
};

// ============================================================================
// USER NOTIFICATIONS API
// ============================================================================

const normalizeNotificationArgs = (userEmailOrPayload, options = {}) => {
  if (userEmailOrPayload && typeof userEmailOrPayload === 'object') {
    const {
      email,
      limit = 50,
      offset = 0,
      typeFilter = null,
      unreadOnly = false
    } = userEmailOrPayload;
    const normalizedType = typeFilter === 'all' ? null : typeFilter;
    return { email, limit, offset, typeFilter: normalizedType, unreadOnly };
  }
  const normalizedType = options.typeFilter === 'all' ? null : options.typeFilter;
  return {
    email: userEmailOrPayload,
    limit: options.limit ?? 50,
    offset: options.offset ?? 0,
    typeFilter: normalizedType ?? null,
    unreadOnly: options.unreadOnly ?? false
  };
};

export const fetchUserNotifications = async (userEmailOrPayload, options = {}) => {
  const { email, limit, offset, typeFilter, unreadOnly } = normalizeNotificationArgs(
    userEmailOrPayload,
    options
  );

  if (!email || typeof email !== 'string') {
    throw new Error('User email is required');
  }

  const response = await callProtectedEndpoint(
    `/api/notifications/user/list`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        limit,
        offset,
        type_filter: typeFilter || null,
        unread_only: unreadOnly
      })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch notifications (HTTP ${response.status})`);
  }

  return await response.json();
};

export const fetchUserNotificationsCount = async (userEmail) => {
  if (!userEmail || typeof userEmail !== 'string') {
    throw new Error('User email is required');
  }
  const response = await callProtectedEndpoint(
    `/api/notifications/user/count`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: userEmail.trim().toLowerCase() })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch notification count (HTTP ${response.status})`);
  }

  return await response.json();
};

// Backward-compatible alias for singular naming used in some branches.
export const fetchUserNotificationCount = fetchUserNotificationsCount;

export const markNotificationRead = async (notificationId) => {
  const response = await callProtectedEndpoint(
    `/api/notifications/${notificationId}/mark-read`,
    { method: 'POST' }
  );

  if (!response.ok) {
    throw new Error(`Failed to mark notification read (HTTP ${response.status})`);
  }

  return await response.json();
};

export const markAllNotificationsRead = async (userEmail) => {
  if (!userEmail || typeof userEmail !== 'string') {
    throw new Error('User email is required');
  }
  const response = await callProtectedEndpoint(
    `/api/notifications/user/mark-all-read`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: userEmail.trim().toLowerCase() })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to mark all notifications read (HTTP ${response.status})`);
  }

  return await response.json();
};

export const markAllUserNotificationsRead = markAllNotificationsRead;

export const registerUserDeviceToken = async (userEmail, payload, platform = 'android') => {
  let email = userEmail;
  let tokenPayload = payload;

  if (payload && typeof payload === 'string') {
    tokenPayload = { token: payload, platform };
  }

  if (payload && typeof payload === 'object' && payload.email) {
    email = payload.email;
    tokenPayload = payload;
  }

  if (!email || typeof email !== 'string') {
    throw new Error('User email is required');
  }

  if (!tokenPayload || typeof tokenPayload !== 'object' || !tokenPayload.token) {
    throw new Error('Device token is required');
  }

  const response = await callProtectedEndpoint(
    `/api/notifications/user/device-token`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email.trim().toLowerCase(),
        token: tokenPayload.token,
        platform: tokenPayload.platform || platform
      })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to register device token (HTTP ${response.status})`);
  }

  return await response.json();
};

export const unregisterUserDeviceToken = async (userEmailOrPayload) => {
  let email = userEmailOrPayload;
  if (userEmailOrPayload && typeof userEmailOrPayload === 'object') {
    email = userEmailOrPayload.email;
  }

  if (!email || typeof email !== 'string') {
    throw new Error('User email is required');
  }

  const response = await callProtectedEndpoint(
    `/api/notifications/user/device-token/remove`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email: email.trim().toLowerCase() })
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to unregister device token (HTTP ${response.status})`);
  }

  return await response.json();
};

// ============================================================================
// LOCATION / PLACES API
// ============================================================================

/**
 * Fetch nearby places (Google Places API via backend)
 */
export const fetchBusinessSuggestions = async (latitude, longitude, radius = 1000) => {
  const url = `${BACKEND_URL}/api/places/nearby?lat=${latitude}&lng=${longitude}&radius=${radius}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: buildHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch business suggestions');
  }
  
  return await response.json();
};

/**
 * Fetch nearest Pye-Barker affiliate office
 */
export const fetchNearestAffiliate = async (latitude, longitude, limit = 1) => {
  const response = await fetch(`${BACKEND_URL}/api/affiliates/nearest?lat=${latitude}&lng=${longitude}&limit=${limit}`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch nearest affiliate');
  }
  
  return await response.json();
};

/**
 * Fetch sales leader notifications (team submissions)
 * Backend: POST /api/admin/notifications
 */
export const fetchSalesLeaderNotifications = async (userEmail, options = {}) => {
  if (!userEmail || typeof userEmail !== 'string') {
    throw new Error('User email is required');
  }

  const email = userEmail.trim().toLowerCase();
  const url = `${BACKEND_URL}/api/admin/notifications`;

  const headers = buildHeaders();

  // Optional bearer token (Entra ID / MSAL)
  if (options && typeof options.accessToken === 'string' && options.accessToken.trim()) {
    headers.Authorization = `Bearer ${options.accessToken.trim()}`;
  }

  // Local-dev bypass header: only attach when running locally.
  // Never rely on this in deployed environments.
  try {
    const isLocal = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost' && !(window.Capacitor?.isNativePlatform?.());
    if (isLocal || options?.forceLocalDevBypass === true) headers['X-Local-Dev'] = 'true';
  } catch (_) {
    // ignore
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch notifications (HTTP ${response.status})`);
  }

  return await response.json();
};

// Duplicate functions removed - already defined above:
// - fetchUserNotifications (line 654)
// - fetchUserNotificationsCount (line 688)
// - fetchUserNotificationCount (line 711 - alias)
// - markAllUserNotificationsRead (line 748 - alias)
// - registerUserDeviceToken (line 750)
// - unregisterUserDeviceToken (line 793)

// ============================================================================
// EXPORT BACKEND_URL FOR LEGACY USE
// ============================================================================
export const getBackendUrl = () => BACKEND_URL;
